import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, handleAxiosError } from 'lib/api';
import type { MutationConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import { isAxiosError } from 'axios';

interface ErrorResponse {
  message: string;
  details?: string;
}

export interface CreativeTextPayload {
  title: string;
  body: string;
  language: string;
  tag: string;
  description?: string;
  default_flows_ids: number[];
  additional_flows_ids: number[];
  localizations: {
    title: string;
    body: string;
    language: string;
    description?: string;
  }[];
}

interface UpdateCreativeTextPayload extends CreativeTextPayload {
  id: number;
}

export interface CreativeTextResponse {
  message: string;
  creative_text_id: number;
}

export const addCreativeText = async (
  params: CreativeTextPayload
): Promise<CreativeTextResponse> => {
  const res = await apiClient.post<CreativeTextResponse>(
    queryKeys.creativeTexts,
    params
  );

  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to add flow');
  }
  return res.data;
};

export const updateCreativeText = async (
  params: UpdateCreativeTextPayload
): Promise<CreativeTextResponse> => {
  const res = await apiClient.put<CreativeTextResponse>(
    `${queryKeys.creativeTexts}/${params.id}`,
    params
  );

  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to add flow');
  }

  return res.data;
};

type MutationFnType = (
  data: CreativeTextPayload
) => ReturnType<typeof addCreativeText>;

type UpdateMutationFnType = (
  data: UpdateCreativeTextPayload
) => ReturnType<typeof updateCreativeText>;

interface UseAddCreativeTextOptions {
  config?: MutationConfig<MutationFnType>;
}

interface UseUpdateCreativeTextOptions {
  config?: MutationConfig<UpdateMutationFnType>;
}

export const useAddCreativeText = ({
  config,
}: UseAddCreativeTextOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.creativeTexts],
        }),
      ]);

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: addCreativeText,
  });
};

export const useUpdateCreativeText = ({
  config,
}: UseUpdateCreativeTextOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.creativeTexts],
        }),
      ]);

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: updateCreativeText,
  });
};
